import { Link, useLocation, useNavigate } from "react-router-dom"
import { appRoutes, canAccessRoute } from "../routes"
import { useAuthUser, useLogout } from "../hooks/useAuth"
import { isFullScope, userRoles } from "../constants"
import routePaths from "../constants/path"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import cn from "../utils/cn"
import Loader from "./Loader"
import { env } from "../config/env"

const Navbar = () => {
  const location = useLocation()
  const { data } = useAuthUser()
  const navigate = useNavigate()
  const { mutate, isPending } = useLogout()

  const user = data?.data ?? null
  const isAuthenticated = isFullScope(data?.scope)

  const visibleRoutes = appRoutes
    ?.filter((route) => route?.isMenuItem && canAccessRoute(route, user?.role))
    ?.map((route) => ({
      ...route,
      children: (route.children ?? []).filter((childRoute) =>
        canAccessRoute(childRoute, user?.role)
      ),
    }))

  const isRouteActive = (route) =>
    route.path
      ? location.pathname === route.path
      : (route.children ?? []).some(
          (childRoute) => childRoute.path === location.pathname
        )

  const handleLogout = () => {
    mutate(undefined, {
      onSuccess: () => {
        navigate(routePaths.login)
      },
    })
  }

  return (
    <nav className="flex items-center py-4 px-8 gap-10 bg-primary shadow-md text-white fixed top-0 left-0 right-0 z-50">
      <div className="flex items-center">
        <img alt="logo" className="h-10" src="/logo.png" />
      </div>
      {isAuthenticated && (
        <div className="flex items-center gap-5 ml-5">
          {visibleRoutes?.map((route) => {
            const hasChildren = route.children?.length > 0
            const isActive = isRouteActive(route)

            return (
              <div
                key={route.path || route.name}
                className="flex flex-col gap-1"
              >
                <div className="relative group">
                  <Menu>
                    <MenuButton
                      className={cn(
                        "group inline-flex items-center gap-0.5 text-sm/6 font-semibold hover:text-secondary focus:outline-none",
                        isActive ? "text-secondary" : "text-white"
                      )}
                    >
                      {route.path ? (
                        <Link to={route.path || "#"}>{route.name}</Link>
                      ) : (
                        route.name
                      )}
                      {hasChildren && (
                        <span className="material-icons text-base transition-transform duration-150 group-data-[open]:rotate-180">
                          expand_more
                        </span>
                      )}
                    </MenuButton>
                    {hasChildren && (
                      <MenuItems
                        transition
                        anchor={{ to: "bottom start", gap: 12 }}
                        className="flex min-w-52 flex-col rounded-xl border border-gray-100 bg-white p-1.5 text-sm/6 shadow-xl shadow-black/10 transition duration-100 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
                        style={{ zIndex: "100" }}
                      >
                        {route.children.map((childRoute) => {
                          const isChildActive =
                            location.pathname === childRoute.path

                          return (
                            <MenuItem key={childRoute.path}>
                              {({ focus }) => (
                                <Link
                                  aria-current={
                                    isChildActive ? "page" : undefined
                                  }
                                  className={cn(
                                    "flex items-center gap-2 rounded-lg px-3 py-2 font-medium transition-colors",
                                    isChildActive
                                      ? "bg-secondary/10 text-secondary"
                                      : "text-gray-700",
                                    focus &&
                                      !isChildActive &&
                                      "bg-gray-100 text-primary"
                                  )}
                                  to={childRoute.path}
                                >
                                  <span
                                    className={cn(
                                      "h-1.5 w-1.5 shrink-0 rounded-full",
                                      isChildActive
                                        ? "bg-secondary"
                                        : "bg-transparent"
                                    )}
                                  />
                                  {childRoute.name}
                                </Link>
                              )}
                            </MenuItem>
                          )
                        })}
                      </MenuItems>
                    )}
                  </Menu>
                </div>
                {isActive && (
                  <div className="w-full h-1 bg-secondary rounded-full"></div>
                )}
              </div>
            )
          })}
        </div>
      )}
      <div className="flex items-center gap-6 ml-auto">
        {isAuthenticated && !user?.role?.includes(userRoles.SUPPORT) && (
          <a
            className="flex items-center text-sm/6 text-white underline cursor-pointer"
            href={env.REACT_APP_TECHNICIAN_PORTAL_URL}
            rel="noopener noreferrer"
            target="_blank"
          >
            Go to Tech Portal
          </a>
        )}
        <button
          className={cn(
            "flex items-center gap-3 bg-secondary px-4 py-2 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
          )}
          disabled={isPending}
          onClick={handleLogout}
        >
          {isPending && <Loader className="h-4 w-4 mb-1" />}
          Logout
        </button>
      </div>
    </nav>
  )
}

export default Navbar
